import { useState } from 'react';
import { ChevronRight, ChevronDown, Layers, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AdminToolModelView } from './AdminToolModelView';
import { AdminPromptConfigView } from './AdminPromptConfigView';
import { AdminPromptTestView } from './AdminPromptTestView';
import { AdminABTestView } from './AdminABTestView';
import { AdminDemoPairsView } from './AdminDemoPairsView';
import { AdminCreditPricingView } from './AdminCreditPricingView';
import { AdminToolFloorplanMaskView } from './AdminToolFloorplanMaskView';
import { AdminFurnitureCatalogView } from './AdminFurnitureCatalogView';
import { AdminMoodboardView } from './AdminMoodboardView';
import { AdminMoodboardChecklistView } from './AdminMoodboardChecklistView';

type StagingSubRoute = 'modelle' | 'prompts' | 'prompt-test' | 'ab-tests' | 'demo-pairs' | 'furniture-catalog' | 'moodboards' | 'checklist' | 'credits';
type FloorplanSubRoute = 'modelle' | 'prompts' | 'credits' | 'mask' | 'prompt-test' | 'ab-tests' | 'demo-pairs';
type ActiveTool = 'staging' | 'floorplan';

const STAGING_ITEMS: Array<{ key: StagingSubRoute; label: string }> = [
  { key: 'modelle',      label: 'Modelle' },
  { key: 'prompts',      label: 'Prompts' },
  { key: 'prompt-test',  label: 'Prompt-Test' },
  { key: 'ab-tests',     label: 'A/B Tests' },
  { key: 'demo-pairs',   label: 'Demo-Paare' },
  { key: 'furniture-catalog', label: 'Möbel-Katalog' },
  { key: 'moodboards', label: 'Moodboards' },
  { key: 'checklist', label: 'Checklisten' },
  { key: 'credits', label: 'Credits' },
];

const FLOORPLAN_ITEMS: Array<{ key: FloorplanSubRoute; label: string; disabled?: boolean }> = [
  { key: 'modelle',     label: 'Modelle' },
  { key: 'prompts',     label: 'Prompts' },
  { key: 'credits',     label: 'Credits' },
  { key: 'mask',        label: 'Mask-Tuning' },
  { key: 'prompt-test', label: 'Prompt-Test', disabled: true },
  { key: 'ab-tests',    label: 'A/B Tests',   disabled: true },
  { key: 'demo-pairs',  label: 'Demo-Paare',  disabled: true },
];

export const ToolSettingsView: React.FC = () => {
  const [tool, setTool] = useState<ActiveTool>('staging');
  const [stagingSub, setStagingSub] = useState<StagingSubRoute>('modelle');
  const [floorplanSub, setFloorplanSub] = useState<FloorplanSubRoute>('modelle');
  const [stagingExpanded, setStagingExpanded] = useState(true);
  const [floorplanExpanded, setFloorplanExpanded] = useState(true);

  return (
    <div className="flex gap-6">
      <nav className="w-64 flex-shrink-0 bg-muted/30 rounded-lg p-2 space-y-1">
        <button
          onClick={() => setStagingExpanded(!stagingExpanded)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted rounded"
        >
          {stagingExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Home className="w-4 h-4" />
          Staging
        </button>
        {stagingExpanded && STAGING_ITEMS.map(item => (
          <button
            key={item.key}
            onClick={() => { setTool('staging'); setStagingSub(item.key); }}
            className={cn(
              'w-full text-left pl-10 pr-3 py-1.5 text-sm rounded hover:bg-muted',
              tool === 'staging' && stagingSub === item.key && 'bg-primary/10 text-primary font-medium',
            )}
          >
            {item.label}
          </button>
        ))}

        <button
          onClick={() => setFloorplanExpanded(!floorplanExpanded)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted rounded mt-2"
        >
          {floorplanExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Layers className="w-4 h-4" />
          Grundriss
        </button>
        {floorplanExpanded && FLOORPLAN_ITEMS.map(item => (
          <button
            key={item.key}
            onClick={() => { if (!item.disabled) { setTool('floorplan'); setFloorplanSub(item.key); } }}
            disabled={item.disabled}
            title={item.disabled ? 'Kommt in Phase D' : undefined}
            className={cn(
              'w-full text-left pl-10 pr-3 py-1.5 text-sm rounded',
              !item.disabled && 'hover:bg-muted',
              item.disabled && 'text-muted-foreground/50 cursor-not-allowed',
              tool === 'floorplan' && floorplanSub === item.key && !item.disabled && 'bg-primary/10 text-primary font-medium',
            )}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className="flex-1 min-w-0">
        {tool === 'staging' && stagingSub === 'modelle' && <AdminToolModelView tool="staging" />}
        {tool === 'staging' && stagingSub === 'prompts' && (
          <AdminPromptConfigView categories={['style', 'constraint', 'aspect_ratio', 'system', 'room_empty', 'room_staged', 'verification', 'stage_refinement']} />
        )}
        {tool === 'staging' && stagingSub === 'prompt-test' && <AdminPromptTestView />}
        {tool === 'staging' && stagingSub === 'ab-tests' && <AdminABTestView />}
        {tool === 'staging' && stagingSub === 'demo-pairs' && <AdminDemoPairsView />}
        {tool === 'staging' && stagingSub === 'furniture-catalog' && <AdminFurnitureCatalogView />}
        {tool === 'staging' && stagingSub === 'moodboards' && <AdminMoodboardView />}
        {tool === 'staging' && stagingSub === 'checklist' && <AdminMoodboardChecklistView />}
        {tool === 'floorplan' && floorplanSub === 'modelle' && <AdminToolModelView tool="floorplan" />}
        {tool === 'floorplan' && floorplanSub === 'prompts' && (
          <AdminPromptConfigView
            categories={[
              'floorplan_2d',
              'floorplan_flooring',
              'floorplan_text_handling',
              'floorplan_room_label',
              'floorplan_3d',
              'floorplan_refinement',
              'floorplan_refinement_clause',
            ]}
          />
        )}
        {tool === 'floorplan' && floorplanSub === 'credits' && <AdminCreditPricingView tool="floorplan" />}
        {tool === 'staging' && stagingSub === 'credits' && <AdminCreditPricingView tool="staging" />}
        {tool === 'floorplan' && floorplanSub === 'mask' && <AdminToolFloorplanMaskView />}
      </div>
    </div>
  );
};
